// ✅ Static metadata export (used in project listing page)
export const metadata = {
  title: 'Modern Front Yard Pavers & Artificial Turf in Toluca Lake, CA 91602',
  description:
    'A modern front yard renovation in Toluca Lake featuring custom pavers, artificial turf, and clean landscape design for enhanced curb appeal by Modern Build.',
  link: '/services/outdoor-living',
  height: 'h-[467px]',
  image:
    '/projects/modern-front-yard-pavers-and-artificial-turf-in-toluca-lake-ca-91602/modern-front-yard-pavers-and-artificial-turf-in-toluca-lake-ca-91602.webp',
};

import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../../components/Header';
import Hero from '../../../components/Hero';
import ContentBlock from '../../../components/ContentBlock';
import { VideoSection } from '../../../components/Videos';
import Gallery from '../../../components/Gallery';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const slug =
    'modern-front-yard-pavers-and-artificial-turf-in-toluca-lake-ca-91602';

  const galleryPath = path.join(
    process.cwd(),
    'public',
    'projects',
    slug,
    'gallery'
  );

  let galleryImages = [];

  // ✅ Load gallery images
  if (fs.existsSync(galleryPath)) {
    const files = fs.readdirSync(galleryPath).filter(file =>
      /\.(jpg|jpeg|png|webp)$/i.test(file)
    );

    galleryImages = files.map(file => ({
      src: `/projects/${slug}/gallery/${file}`,
      alt: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
      title: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
    }));
  }

  return {
    props: {
      galleryImages,
    },
  };
}

export default function ModernFrontYardTolucaLake({
  galleryImages,
}) {
  const meta = metadata;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title}/>
        <meta name="description" content={meta.description} />
        <meta name="author" content="Modern Build Home Remodeling" />

        <meta
          name="keywords"
          content="front yard renovation Toluca Lake, pavers installation 91602, artificial turf landscaping, modern driveway pavers, curb appeal Los Angeles, Modern Build"
        />

        <link
          rel="canonical"
          href="https://modernbuildremodel.com/projects/modern-front-yard-pavers-and-artificial-turf-in-toluca-lake-ca-91602"
        />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta
          property="og:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/projects/modern-front-yard-pavers-and-artificial-turf-in-toluca-lake-ca-91602"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:site_name"
          content="Modern Build Home Remodeling"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={meta.title}
        />
        <meta
          name="twitter:description"
          content={meta.description}
        />
        <meta
          name="twitter:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />

        {/* Breadcrumb Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://modernbuildremodel.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Projects',
                  item: 'https://modernbuildremodel.com/projects',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name:
                    'Modern Front Yard Pavers & Artificial Turf in Toluca Lake CA 91602',
                  item:
                    'https://modernbuildremodel.com/projects/modern-front-yard-pavers-and-artificial-turf-in-toluca-lake-ca-91602',
                },
              ],
            }),
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title:
                  'Modern Front Yard Pavers & Artificial Turf in Toluca Lake, CA 91602 | Modern Build',
                description:
                  'Explore a modern front yard renovation in Toluca Lake featuring custom pavers, artificial turf, and clean landscape design.',
                slug:
                  '/projects/modern-front-yard-pavers-and-artificial-turf-in-toluca-lake-ca-91602',
              })
            ),
          }}
        />
      </Head>

      <Header />

      <Hero
        category="modernFrontYardTolucaLake91602"
        page="page"
        hasContentH1={false}
      />

      <ContentBlock
        subtitle="PROJECT OVERVIEW"
        title="Modern Front Yard Renovation in Toluca Lake"
        description="This front yard renovation in Toluca Lake transformed an outdated landscape into a modern, clean, and low-maintenance outdoor space with custom pavers and artificial turf."
        extraText="The new design enhances curb appeal while improving usability and long-term durability."
        buttonText="Start My Outdoor Project"
        buttonLink="/services/outdoor-living"
      />

      <Gallery images={galleryImages} />

      <section className="bg-white py-[120px]">
        <div className="container mx-auto px-6">
          <VideoSection
            videoUrl="https://www.youtube.com/embed/rsHQoOqovjU"
            serviceType="Front Yard Renovation"
            location="Toluca Lake, CA"
            description="Watch how this front yard in Toluca Lake was transformed with modern pavers and artificial turf."
            serviceLink="/services/outdoor-living"
            coverImage={meta.image}
            isVideoLeft={true}
          />
        </div>
      </section>

      <section className="bg-white py-[120px]">
  <div className="container mx-auto px-6 max-w-4xl">

    <h2 className="text-2xl font-light text-primary leading-tight font-gilda mb-4">
      Modern Front Yard Pavers & Artificial Turf in Toluca Lake, CA 91602
    </h2>

    <p className="text-gray font-light text-lg leading-relaxed">
      This modern front yard renovation in Toluca Lake, CA 91602 demonstrates how thoughtful landscape design can dramatically improve curb appeal and property value. The original yard lacked visual structure and required ongoing maintenance. Our team reimagined the space using <a
        href="https://modernbuildremodel.com/outdoor-living/california/toluca-lake/91602"
        className="text-primary underline"
      >
        custom outdoor living solutions
      </a>, including pavers and turf, to create a functional and visually balanced exterior.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      The combination of durable pavers and professionally installed <a
        href="https://modernbuildremodel.com/artificial-turf/california/toluca-lake/91602"
        className="text-primary underline"
      >
        artificial turf
      </a> ensures low maintenance while enhancing the home’s aesthetic appeal. This front yard demonstrates the quality and precision of our craftsmanship.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Project Highlights
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The renovation includes premium concrete pavers for walkways and parking areas, professionally installed artificial turf for year-round greenery, and integrated borders that define each landscape zone.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      These upgrades provide a durable, low-maintenance solution while maintaining a refined and modern appearance.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Design Strategy
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The design strategy focused on maximizing visual impact while reducing long-term upkeep. Neutral-toned pavers were selected to complement the home’s exterior, while artificial turf was placed to soften the overall look.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Proper grading and drainage were also implemented to ensure long-term performance and surface stability.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Results and Homeowner Experience
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The completed front yard now presents a clean, inviting, and modern appearance. The home stands out on the street with improved symmetry, color balance, and functionality.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Homeowners benefit from reduced maintenance, enhanced aesthetics, and increased resale value.
    </p>

  </div>
</section>


      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
